import type { GameState, Space, Survivor, WorkTask } from "../types";
import {
  resolvedSurvivorActivity,
  survivorActivityTarget,
  survivorDutySpaceId,
  survivorVisualActivity,
} from "./scheduling";

export type SurvivorMotion =
  | "idle"
  | "walk"
  | "climb"
  | "labour"
  | "precision"
  | "social"
  | "guard"
  | "rest"
  | "carry"
  | "injured"
  | "dead";

export type SurvivorRigProp = "none" | "hammer" | "rifle" | "pouch" | "radio";

export interface SurvivorPresentation {
  survivor: Survivor;
  activity: string;
  motion: SurvivorMotion;
  prop: SurvivorRigProp;
  targetSpaceId: string | null;
  facing: 1 | -1;
  phase: number;
  injury: number;
  transitionKey: string;
}

const LABOUR_ACTIVITIES = [
  "repair",
  "build",
  "expand",
  "upgrade",
  "clear",
  "excavate",
  "generator",
  "workshop",
  "engineering",
  "service",
  "reclamation",
  "salvage",
];
const PRECISION_ACTIVITIES = [
  "kitchen",
  "cook",
  "hydroponics",
  "infirmary",
  "medicine",
  "radio",
  "research",
];
const GUARD_ACTIVITIES = ["combat", "entrance", "armoury", "security", "guard"];
const REST_ACTIVITIES = ["dormitory", "rest", "sleep"];
const CARRY_ACTIVITIES = ["storage", "logistics", "carry"];
const WALK_ACTIVITIES = ["away", "travel", "walk", "scout", "outbound", "returning"];
const SOCIAL_ACTIVITIES = ["social", "talk", "eat", "drink", "comfort"];

export function stableSurvivorHash(...parts: Array<string | number | null | undefined>): number {
  const source = parts.map((part) => String(part ?? "")).join(":");
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function includesAny(activity: string, choices: string[]): boolean {
  return choices.some((choice) => activity.includes(choice));
}

export function motionForActivity(activity: string, survivor?: Survivor): SurvivorMotion {
  if (survivor && (!survivor.alive || survivor.status === "dead")) return "dead";
  if (survivor && survivor.health < 35) return "injured";
  const normalised = activity.toLowerCase().replaceAll("_", " ");
  if (normalised.includes("injured")) return "injured";
  if (includesAny(normalised, WALK_ACTIVITIES)) return "walk";
  if (includesAny(normalised, LABOUR_ACTIVITIES)) return "labour";
  if (includesAny(normalised, PRECISION_ACTIVITIES)) return "precision";
  if (includesAny(normalised, SOCIAL_ACTIVITIES)) return "social";
  if (includesAny(normalised, GUARD_ACTIVITIES)) return "guard";
  if (includesAny(normalised, REST_ACTIVITIES)) return "rest";
  if (includesAny(normalised, CARRY_ACTIVITIES)) return "carry";
  return "idle";
}

function propFromEquipment(equipment: string[]): SurvivorRigProp | null {
  for (const item of equipment) {
    const normalised = item.toLowerCase().replaceAll("_", " ");
    if (["rifle", "shotgun", "firearm", "weapon"].some((key) => normalised.includes(key))) return "rifle";
    if (["hammer", "crowbar", "pickaxe", "axe", "tool"].some((key) => normalised.includes(key))) return "hammer";
    if (["radio", "handset", "receiver"].some((key) => normalised.includes(key))) return "radio";
    if (["medkit", "medical", "first aid", "pouch"].some((key) => normalised.includes(key))) return "pouch";
  }
  return null;
}

export function propForActivity(activity: string, survivor: Survivor): SurvivorRigProp {
  const equipped = propFromEquipment(survivor.equipment);
  if (equipped) return equipped;
  const normalised = activity.toLowerCase();
  if (includesAny(normalised, GUARD_ACTIVITIES)) return "rifle";
  if (includesAny(normalised, LABOUR_ACTIVITIES)) return "hammer";
  if (normalised.includes("radio") || normalised.includes("research")) return "radio";
  if (normalised.includes("eat") || normalised.includes("drink")) return "pouch";
  if (includesAny(normalised, PRECISION_ACTIVITIES)) return "pouch";
  return "none";
}

export function survivorInjuryIntensity(survivor: Survivor): number {
  const health = Math.max(0, Math.min(100, survivor.health));
  const healthLoss = (100 - health) / 100;
  if (health >= 90) return healthLoss;
  const worstRecorded = survivor.injuries.reduce(
    (worst, injury) => Math.max(worst, Math.max(0, Math.min(100, injury.severity)) / 200),
    0,
  );
  return Math.max(healthLoss, worstRecorded);
}

function viableRoom(space: Space): boolean {
  return Boolean(
    space.discovered
    && space.state === "excavated"
    && space.room
    && space.room.status !== "trashed",
  );
}

function preferredAnchorSpaces(state: GameState, preferredTypes: string[]): Space[] {
  const rooms = state.site.spaces.filter(viableRoom);
  const preferred = rooms.filter((space) => preferredTypes.includes(space.room!.type));
  return (preferred.length ? preferred : rooms).sort((left, right) => left.id.localeCompare(right.id));
}

function deterministicAnchor(
  state: GameState,
  survivor: Survivor,
  preferredTypes: string[],
): string | null {
  const candidates = preferredAnchorSpaces(state, preferredTypes);
  if (!candidates.length) {
    return state.site.spaces.find((space) => space.discovered)?.id ?? null;
  }
  const index = stableSurvivorHash(state.site.visual_seed, survivor.id, ...preferredTypes) % candidates.length;
  return candidates[index]!.id;
}

function socialAnchor(state: GameState, survivor: Survivor): string | null {
  const relationship = (state.relationships ?? [])
    .filter((item) => item.survivor_ids.includes(survivor.id))
    .filter((item) => item.bond + item.trust - item.tension > 45)
    .sort((left, right) => (
      right.bond + right.trust - right.tension
      - (left.bond + left.trust - left.tension)
    ))[0];
  if (!relationship) return null;
  const companionId = relationship.survivor_ids.find((id) => id !== survivor.id);
  const companion = state.survivors.find((item) => item.id === companionId && item.alive && item.status === "home");
  if (!companion) return null;
  const companionDuty = survivorDutySpaceId(companion);
  if (companionDuty) {
    const room = state.site.spaces.find((space) => space.id === companionDuty);
    if (room && viableRoom(room)) return room.id;
  }
  return deterministicAnchor(state, survivor, ["kitchen", "dormitory"]);
}

function taskPresentation(
  state: GameState,
  survivor: Survivor,
  task: WorkTask | undefined,
): { activity: string; targetSpaceId: string | null } {
  if (!task) {
    return {
      activity: "idle",
      targetSpaceId: deterministicAnchor(state, survivor, ["dormitory", "kitchen", "entrance"]),
    };
  }
  if (task.space_id) return { activity: task.kind, targetSpaceId: task.space_id };
  if (task.kind === "relocation") {
    return {
      activity: "logistics",
      targetSpaceId: deterministicAnchor(state, survivor, ["storage", "entrance"]),
    };
  }
  return {
    activity: task.kind,
    targetSpaceId: deterministicAnchor(state, survivor, ["radio"]),
  };
}

export function resolveSurvivorPresentation(
  state: GameState,
  survivor: Survivor,
): SurvivorPresentation {
  let activity = "idle";
  let targetSpaceId: string | null = null;

  if (survivor.current_activity) {
    const projected = resolvedSurvivorActivity(survivor);
    activity = survivorVisualActivity(state, survivor);
    if (projected.kind === "rest" && projected.rough_sleeping) {
      activity = "rough_sleep";
    }
    targetSpaceId = survivorActivityTarget(state, survivor);
    if (projected.kind === "task_work" && !targetSpaceId) {
      targetSpaceId = deterministicAnchor(state, survivor, ["radio"]);
    } else if ((projected.kind === "rest" || projected.kind === "treatment") && !targetSpaceId) {
      targetSpaceId = deterministicAnchor(
        state,
        survivor,
        projected.kind === "rest" ? ["dormitory"] : ["infirmary"],
      );
    } else if (projected.kind === "idle") {
      targetSpaceId = deterministicAnchor(state, survivor, ["dormitory", "kitchen", "entrance"]);
    }
  } else if (!survivor.alive || survivor.status === "dead") {
    activity = "dead";
  } else if (survivor.status === "away") {
    const expedition = survivor.assignment?.kind === "expedition"
      ? state.expeditions.find((item) => item.id === survivor.assignment?.id)
      : undefined;
    activity = expedition?.phase ?? "away";
  } else if (survivor.assignment?.kind === "task") {
    const task = state.tasks.find((item) => item.id === survivor.assignment?.id);
    ({ activity, targetSpaceId } = taskPresentation(state, survivor, task));
  } else if (survivor.assignment?.kind === "room") {
    const space = state.site.spaces.find((item) => item.id === survivor.assignment?.id);
    if (space?.room && viableRoom(space)) {
      targetSpaceId = space.id;
      activity = space.room.type;
    } else {
      targetSpaceId = deterministicAnchor(state, survivor, ["dormitory", "kitchen", "entrance"]);
    }
  } else {
    const exhausted = survivor.fatigue >= 70;
    const thirsty = survivor.thirst >= 66;
    const hungry = survivor.hunger >= 66;
    const needsCompany = survivor.morale <= 48;
    const companionSpace = needsCompany ? socialAnchor(state, survivor) : null;
    targetSpaceId = companionSpace ?? deterministicAnchor(
      state,
      survivor,
      exhausted
        ? ["dormitory"]
        : thirsty
          ? ["water_recycler", "kitchen"]
          : hungry
            ? ["kitchen"]
            : ["dormitory", "kitchen", "entrance"],
    );
    const target = state.site.spaces.find((space) => space.id === targetSpaceId);
    if (companionSpace) activity = "comfort";
    else if (exhausted || target?.room?.type === "dormitory") activity = "rest";
    else if (thirsty) activity = "drink";
    else if (hungry) activity = "eat";
    else if (target?.room?.type === "kitchen") activity = "social";
    else if (target?.room?.type === "entrance") activity = "watch";
  }

  const phase = Math.max(0, Math.min(999, survivor.appearance?.motion_phase ?? 0)) / 1000;
  const facing = stableSurvivorHash(
    state.site.visual_seed,
    survivor.id,
    targetSpaceId,
    activity,
  ) % 2 === 0 ? 1 : -1;
  const motion = motionForActivity(activity, survivor);
  const assignmentKey = survivor.current_activity
    ? [
        survivor.current_activity.kind,
        survivor.current_activity.room_id,
        survivor.current_activity.task_id,
        survivor.current_activity.facility_space_id,
      ].join(":")
    : survivor.assignment
      ? `${survivor.assignment.kind}:${survivor.assignment.id}`
      : "unassigned";

  return {
    survivor,
    activity,
    motion,
    prop: propForActivity(activity, survivor),
    targetSpaceId,
    facing,
    phase,
    injury: survivorInjuryIntensity(survivor),
    transitionKey: [
      state.site.number,
      state.site.visual_seed,
      survivor.id,
      survivor.status,
      assignmentKey,
      activity,
      targetSpaceId ?? "offsite",
    ].join(":"),
  };
}
